import { LineSeries, ResponsiveLine, SliceData } from "@nivo/line";
import { useWindowSize } from "hooks/useWindowSize";
import classNames from "classnames";
import { colors } from "constants/colors";
import dayjs from "dayjs";
import { EmptyMetrics } from "components/EmptyMetrics/EmptyMetrics";
import { EmptyCashBalanceMetrics } from "components/Illustrations/EmptyCashBalanceMetrics";
import { motion } from "framer-motion";
import { ReactNode, useState } from "react";
import { formatAmount } from "utils/foramtAmount";
import { MMM_YY } from "constants/date";

const getNiceTickInterval = (range: number): number => {
  if (range <= 0) return 1000;
  const magnitude = Math.pow(10, Math.floor(Math.log10(range)));
  const normalized = range / magnitude;
  if (normalized <= 2) return magnitude / 2;
  if (normalized <= 5) return magnitude;
  return magnitude * 2;
};

const formatAxisValue = (value: number | string): string => {
  const num = Number(value);
  const absValue = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  if (absValue >= 1_000_000) {
    return `${sign}$${(absValue / 1_000_000).toFixed(1)}M`;
  }
  if (absValue >= 1_000) {
    return `${sign}$${Math.round(absValue / 1_000)}K`;
  }
  return `${sign}$${absValue}`;
};

interface Point {
  id: string | number;
  x: number;
  y: number;
}

interface LayerProps {
  points: ReadonlyArray<Point>;
}

export const Crosshair = ({
  hoverSectionId,
  ...props
}: LayerProps & {
  hoverSectionId: string;
}) => {
  const { points } = props;

  return (
    <g>
      {points.map(({ id, x, y }) => {
        return (
          <motion.line
            layout
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "100%" }}
            transition={{ duration: 0.1, delay: 0.1, ease: "easeInOut" }}
            key={id}
            className={classNames({
              "t-hidden": id !== hoverSectionId,
              "t-block": id === hoverSectionId,
            })}
            x1={x}
            x2={x}
            y1={y}
            y2="79%"
            stroke={colors.purple[40]}
            strokeWidth={1}
          />
        );
      })}
    </g>
  );
};

type ChartData = { x: string; y: number }[];

type MetricChartProps = {
  title: string;
  seriesId: string;
  gradientId: string;
  chartData: ChartData;
  renderTooltip: (slice: SliceData<LineSeries>) => ReactNode;
  yAxisFormat?: (v: number | string) => string;
  emptyTitle?: string;
  emptySubtitle?: string;
};

export const MetricChart = ({
  title,
  seriesId,
  gradientId,
  chartData,
  renderTooltip,
  yAxisFormat = formatAxisValue,
  emptyTitle = "No transactions categorised",
  emptySubtitle = "Add transactions to see revenue breakdown",
}: MetricChartProps) => {
  const { isMobile } = useWindowSize();
  const [hoverSectionId, setHoverPoint] = useState("");

  const data = [{ id: seriesId, data: chartData }];
  const isEmpty = chartData.length === 0;

  const allValues = chartData.map((d) => d.y);
  const dataMin = Math.min(...allValues, 0);
  const dataMax = Math.max(...allValues, 0);
  const range = dataMax - dataMin;
  const isAllZero = range === 0;
  const tickInterval = isAllZero ? 20 : getNiceTickInterval(range);
  const scaleMin = isAllZero
    ? 0
    : Math.floor(dataMin / tickInterval) * tickInterval;
  const scaleMax = isAllZero
    ? 100
    : Math.ceil(dataMax / tickInterval) * tickInterval;

  const tickValues: number[] = [];
  for (let i = scaleMin; i <= scaleMax; i += tickInterval) {
    tickValues.push(i);
  }

  return (
    <div className="t-flex t-flex-col t-border t-border-solid t-border-neutral-10 t-rounded-lg t-bg-surface">
      <div className="t-px-6 sm:t-px-4 t-py-4 t-border-0 t-border-b t-border-solid t-border-neutral-10">
        <span className="t-text-subtext t-text-text-100">{title}</span>
      </div>
      <div className="t-px-4 t-py-5 t-w-full t-h-80">
        {isEmpty ? (
          <EmptyMetrics
            illustration={<EmptyCashBalanceMetrics />}
            title={emptyTitle}
            subtitle={emptySubtitle}
          />
        ) : (
          <ResponsiveLine
            key={title}
            onMouseMove={(p) => {
              // @ts-ignore
              const [point] = p.points;
              setHoverPoint(point?.id);
            }}
            onMouseLeave={() => setHoverPoint("")}
            margin={{
              bottom: isMobile ? 55 : 30,
              left: 40,
              right: 20,
              top: 30,
            }}
            xScale={{ type: "point" }}
            yScale={{ type: "linear", min: scaleMin, max: scaleMax }}
            enableArea={true}
            data={data}
            pointColor={colors.purple[40]}
            defs={[
              {
                id: gradientId,
                type: "linearGradient",
                colors: [
                  { offset: 0, color: colors.purple[30] },
                  { offset: 100, color: colors.purple[0] },
                ],
                gradientTransform: "rotate(350 0.5 0.5)",
              },
            ]}
            fill={[{ match: { id: seriesId }, id: gradientId }]}
            colors={[colors.purple[40]]}
            enableGridX={false}
            enableGridY={true}
            gridYValues={tickValues}
            theme={{
              grid: {
                line: {
                  stroke: colors.neutral[0],
                  strokeWidth: 1,
                },
              },
              axis: {
                ticks: {
                  text: {
                    fill: colors.text[30],
                    fontSize: 12,
                  },
                },
              },
            }}
            pointLabel="data.yFormatted"
            pointSize={6}
            lineWidth={1.5}
            useMesh={true}
            axisBottom={{
              tickSize: 0,
              tickPadding: 10,
              tickRotation: isMobile ? MOBILE_TICK_ROTATION : 0,
              format: (value) => dayjs(value).format(MMM_YY),
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 10,
              tickValues,
              format: yAxisFormat,
            }}
            sliceTooltip={({ slice }) => <>{renderTooltip(slice)}</>}
            enableSlices="x"
            layers={[
              "grid",
              "markers",
              "axes",
              "areas",
              (props) => (
                <Crosshair {...props} hoverSectionId={hoverSectionId} />
              ),
              "lines",
              "slices",
              "points",
              "mesh",
              "legends",
            ]}
          />
        )}
      </div>
    </div>
  );
};

// Vertical labels so period names don't overlap on narrow screens.
const MOBILE_TICK_ROTATION = -90;

export const RevenueChart = ({
  title,
  chartData,
}: {
  title: string;
  chartData: ChartData;
}) => {
  return (
    <MetricChart
      title={title}
      seriesId="revenue"
      gradientId="revenueArea"
      chartData={chartData}
      renderTooltip={(slice) => (
        <div className="t-bg-neutral-100 t-p-3 t-rounded t-text-subtext-sm t-text-white t-min-w-56 sm:t-min-w-40 t-min-h-16 t-flex t-flex-col t-gap-2 t-justify-center t-items-center">
          <div>{slice.points[0].data.xFormatted}</div>
          <div className="t-flex t-gap-2">
            <div className="t-w-4 t-h-4 t-rounded-sm t-bg-revenue-chart-gradient" />
            {title}: {formatAmount(Number(slice.points[0].data.y))}
          </div>
        </div>
      )}
    />
  );
};
